import React, { useState, useEffect } from 'react';

const AdminCommunication = () => {
    const [syncedEmails, setSyncedEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSendEmail, setShowSendEmail] = useState(false);
    const [emailForm, setEmailForm] = useState({
        to: '',
        subject: '',
        message: ''
    });

    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    const getAuthHeaders = React.useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }, []);

    const loadSyncedEmails = React.useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/communication/synced-emails`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            setSyncedEmails(data);
        } catch (error) {
            console.error('Error loading emails:', error);
        } finally {
            setLoading(false);
        }
    }, [API_URL, getAuthHeaders]);

    useEffect(() => {
        loadSyncedEmails();
    }, [loadSyncedEmails]);

    const syncEmails = async () => {
        try {
            const response = await fetch(`${API_URL}/api/communication/sync-emails`, {
                method: 'POST',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                alert('Email sync started!');
                setTimeout(loadSyncedEmails, 2000);
            }
        } catch (error) {
            console.error('Error syncing emails:', error);
            alert('Error syncing emails');
        }
    };

    const sendEmail = async (e) => {
        e.preventDefault();

        try {
            const response = await fetch(`${API_URL}/api/communication/send-email`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(emailForm)
            });

            if (response.ok) {
                alert('Email sent successfully!');
                setShowSendEmail(false);
                setEmailForm({ to: '', subject: '', message: '' });
            } else {
                const error = await response.json();
                alert('Error: ' + (error.detail || 'Failed to send email'));
            }
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Error sending email');
        }
    };

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Communication</h2>
                <div className="flex space-x-3">
                    <button
                        onClick={() => setShowSendEmail(true)}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
                    >
                        <i className="fas fa-envelope mr-2"></i>Send Email
                    </button>
                    <button
                        onClick={syncEmails}
                        className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700"
                    >
                        <i className="fas fa-sync mr-2"></i>Sync Emails
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-4 bg-gray-50 border-b">
                    <h3 className="font-semibold">Synced Emails</h3>
                </div>
                <div className="divide-y divide-gray-200">
                    {syncedEmails.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                            No synced emails found
                        </div>
                    ) : (
                        syncedEmails.map(email => (
                            <div key={email.id} className="p-4 hover:bg-gray-50">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <div className="flex items-center space-x-2 mb-1">
                                            <span className="font-semibold">{email.from}</span>
                                            <span className="text-xs text-gray-500">
                                                {new Date(email.date).toLocaleString()}
                                            </span>
                                        </div>
                                        <div className="text-sm font-medium text-gray-900 mb-1">
                                            {email.subject}
                                        </div>
                                        <div className="text-sm text-gray-600 line-clamp-2">
                                            {email.textBody || email.htmlBody?.substring(0, 150)}
                                        </div>
                                    </div>
                                    {email.attachments && email.attachments.length > 0 && (
                                        <div className="ml-4">
                                            <i className="fas fa-paperclip text-gray-400"></i>
                                            <span className="text-xs text-gray-500 ml-1">
                                                {email.attachments.length}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Send Email Modal */}
            {showSendEmail && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-2xl font-bold">Send Email</h3>
                            <button
                                onClick={() => setShowSendEmail(false)}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <i className="fas fa-times text-2xl"></i>
                            </button>
                        </div>

                        <form onSubmit={sendEmail} className="space-y-4">
                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">To *</label>
                                <input
                                    type="email"
                                    value={emailForm.to}
                                    onChange={(e) => setEmailForm({ ...emailForm, to: e.target.value })}
                                    required
                                    className="w-full px-4 py-2 border rounded-lg"
                                    placeholder="recipient@example.com"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Subject *</label>
                                <input
                                    type="text"
                                    value={emailForm.subject}
                                    onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                                    required
                                    className="w-full px-4 py-2 border rounded-lg"
                                    placeholder="Email subject"
                                />
                            </div>

                            <div>
                                <label className="block text-gray-700 font-semibold mb-2">Message *</label>
                                <textarea
                                    value={emailForm.message}
                                    onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                                    required
                                    rows="8"
                                    className="w-full px-4 py-2 border rounded-lg"
                                    placeholder="Email message"
                                />
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t">
                                <button
                                    type="button"
                                    onClick={() => setShowSendEmail(false)}
                                    className="px-6 py-2 border rounded-lg hover:bg-gray-100"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                >
                                    <i className="fas fa-paper-plane mr-2"></i>Send
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminCommunication;
